import {
  type DryRunChartReport,
  type DryRunFailure,
  type DryRunReport,
  type DryRunStageReport,
} from "@/shared/domain/boards/boardAuthoringService";

import {
  toolError,
  ToolErrorKind,
  WHEN_THE_REQUEST_FAILS_AGAIN,
  type ClientToolExecutionResult,
} from "./ClientTools";

/** One stage in a create or update result: its id, its name, and what the dry run counted. */
interface StageOutcome {
  /** Null for a stage that declares no id. */
  id: string | null;
  name: string;
  kind: string;
  /** Absent when the dry run counted no rows, as for topic data. */
  rows?: number;
}

/**
 * What a create or update returns to the model from a dry run that passed.
 * The dry-run report itself is not repeated on success, so this carries
 * everything in it the model still needs.
 */
export interface DryRunOutcome {
  /** Each panel with the stages it binds; `warnings` joins the panel's own and those of its stages. */
  panels: {
    /** Null for a panel that declares no id. */
    id: string | null;
    kind: string;
    status: string;
    stages: StageOutcome[];
    warnings: string[];
    /** For a chart that ran: the views that show no tooltip, each with the reason. */
    tooltips?: DryRunChartReport["tooltips"];
    /** For a chart that ran: the legends whose entries select series. */
    selectable_legends?: DryRunChartReport["selectable_legends"];
  }[];
  /** The stages no panel binds, which ran and were saved all the same. Absent when every stage is bound. */
  unbound_stages?: (StageOutcome & { warnings: string[] })[];
  /** Warnings about the board as a whole. */
  warnings: string[];
  /** The board time range the stages ran over, as ISO 8601 start and end times in the board's
   *  time zone with its offset. */
  resolved_time_range?: { start: string; end: string };
}

function stageOutcome(stage: DryRunStageReport): StageOutcome {
  return {
    id: stage.stage,
    name: stage.stageName,
    kind: stage.kind,
    ...(stage.rows !== undefined ? { rows: stage.rows } : {}),
  };
}

/** The members a create or update result takes from the report of a dry run that passed. */
export function dryRunOutcome(report: DryRunReport): DryRunOutcome {
  const byId = new Map(report.stages.map((stage) => [stage.stage, stage]));
  const bound = new Set(report.panels.flatMap((panel) => panel.stages));
  const unbound = report.stages.filter(
    (stage) => stage.stage === null || !bound.has(stage.stage),
  );
  return {
    panels: report.panels.map((panel) => {
      const stages = panel.stages.flatMap((id) => {
        const stage = byId.get(id);
        return stage === undefined ? [] : [stage];
      });
      return {
        id: panel.id,
        kind: panel.kind,
        status: panel.status,
        stages: stages.map(stageOutcome),
        warnings: [
          ...panel.warnings,
          ...stages.flatMap((stage) => stage.warnings ?? []),
        ],
        ...(panel.chart?.tooltips !== undefined
          ? { tooltips: panel.chart.tooltips }
          : {}),
        ...(panel.chart?.selectable_legends !== undefined
          ? { selectable_legends: panel.chart.selectable_legends }
          : {}),
      };
    }),
    ...(unbound.length > 0
      ? {
          unbound_stages: unbound.map((stage) => ({
            ...stageOutcome(stage),
            warnings: stage.warnings ?? [],
          })),
        }
      : {}),
    warnings: report.warnings,
    ...(report.resolved_time_range !== undefined
      ? { resolved_time_range: report.resolved_time_range }
      : {}),
  };
}

/**
 * Where a blocked dry-run report keeps its failures, in the words every
 * result carrying one uses. A definition whose top-level members cannot be
 * read has empty `stages` and `panels`, so `definition_problems` is named
 * first. The member names are those of `DryRunReport`.
 */
export const WHERE_A_REPORT_KEEPS_ITS_FAILURES =
  "When report.definition_problems is present, the definition's top-level " +
  "members could not be read: it lists why, and report.stages and " +
  "report.panels are empty because nothing ran. Otherwise each failing " +
  "stage is in report.stages with its failure, and each failing panel is " +
  "in report.panels with its parse failure, failures or chart errors.";

/**
 * Whether every stage and panel failure of a report carries
 * `rerun_can_pass`, so the same call can pass when made again.
 * `stage-input-failed` is left out: it only repeats the failure of a stage it
 * reads. False for a report with no other failure, such as one that passed
 * or one with `definition_problems`, whose stages and panels are empty.
 */
export function blockedOnlyByFailuresARerunCanPass(
  report: DryRunReport,
): boolean {
  const ownFailures = [
    ...report.stages.flatMap((stage) =>
      stage.failure === undefined ? [] : [stage.failure],
    ),
    ...report.panels.flatMap((panel) =>
      panel.parse === undefined
        ? panel.failures
        : [panel.parse, ...panel.failures],
    ),
  ].filter((failure) => failure.kind !== "stage-input-failed");
  return (
    ownFailures.length > 0 &&
    ownFailures.every((failure) => failure.rerun_can_pass === true)
  );
}

/**
 * What the model is told to do with a report for which
 * {@link blockedOnlyByFailuresARerunCanPass} is true. It is the note of a dry
 * run's result, and ends the error of a create or update that such a report
 * blocked (see {@link blockedWriteError}), so both say the same thing.
 */
export const CALL_AGAIN_WHEN_A_RERUN_CAN_PASS =
  "Every failure in report.stages and report.panels carries " +
  "rerun_can_pass, except stage-input-failed, which repeats the failure of " +
  "a stage it reads. Call once more with the same input. " +
  WHEN_THE_REQUEST_FAILS_AGAIN;

/**
 * A dry-run report as the chat receives it: without the sentences written
 * for the person on the confirmation card (`besideStage`, `besidePanel`,
 * and the members and reasons of a definition that could not be read),
 * since every failure's `message` and `issues` already say the same to the
 * chat in its own terms.
 */
export function reportForTheChat(report: DryRunReport): DryRunReport {
  const {
    definition_problem_members: _members,
    definition_problem_reasons: _reasons,
    ...rest
  } = report;
  return {
    ...rest,
    stages: report.stages.map((stage) =>
      stage.failure === undefined
        ? stage
        : { ...stage, failure: failureForTheChat(stage.failure) },
    ),
    panels: report.panels.map((panel) => ({
      ...panel,
      ...(panel.parse === undefined
        ? {}
        : { parse: failureForTheChat(panel.parse) }),
      failures: panel.failures.map(failureForTheChat),
      ...(panel.saved_board_faults === undefined
        ? {}
        : {
            saved_board_faults: panel.saved_board_faults.map(failureForTheChat),
          }),
    })),
  };
}

function failureForTheChat(failure: DryRunFailure): DryRunFailure {
  const { besideStage: _stage, besidePanel: _panel, ...rest } = failure;
  return rest;
}

/**
 * The error of a create or update whose dry run blocked it. `nothingWritten`
 * says that the call changed nothing. When a rerun can pass every failure,
 * the error is `UNAVAILABLE` and says to call again unchanged; otherwise it
 * is `VALIDATION` and ends with `repair`, which says what to fix.
 */
export function blockedWriteError(
  report: DryRunReport,
  nothingWritten: string,
  repair: string,
): ClientToolExecutionResult {
  return blockedOnlyByFailuresARerunCanPass(report)
    ? toolError(
        `${nothingWritten} ${CALL_AGAIN_WHEN_A_RERUN_CAN_PASS}`,
        { report: reportForTheChat(report) },
        ToolErrorKind.UNAVAILABLE,
      )
    : toolError(
        `${nothingWritten} ${WHERE_A_REPORT_KEEPS_ITS_FAILURES} ${repair}`,
        { report: reportForTheChat(report) },
        ToolErrorKind.VALIDATION,
      );
}
